// Version applicative bakée dans le bundle, comparée au runtime à
// `public/version.json` (servi frais, jamais mis en cache) pour détecter un
// nouveau déploiement.
//
// BASELINE FLOTTE : toute app repart à 2.1 (docs/standards/version-button.md).
//
// CONVENTION, à appliquer à chaque lot visible :
//   • changement PROFOND (archi, refonte, grosse feature) → majeur : 2.x → 3.0
//   • simple ajout ou petit correctif                     → mineur : 2.1 → 2.2
// Toujours décrire ce qui change dans le changelog — sinon rien n'est annoncé.
//
// ⚠️ EXCLUSIF avec le mécanisme à hash de commit. `version.json` porte ici un
// SEMVER commité, comparé segment par segment. Si le netlify.toml y écrivait un
// `$COMMIT_REF`, `parseInt("e7822c5a…")` vaudrait NaN, la comparaison rendrait
// toujours 0, et AUCUNE mise à jour ne serait jamais détectée — sans erreur ni
// log, la bannière ne s'afficherait simplement jamais.
export const APP_VERSION = "2.18";

/** Compare « x.y[.z] » : >0 si a>b, <0 si a<b, 0 si égal. */
export function compareVersions(a, b) {
  const pa = String(a).split(".").map((n) => parseInt(n, 10) || 0);
  const pb = String(b).split(".").map((n) => parseInt(n, 10) || 0);
  const len = Math.max(pa.length, pb.length);
  for (let i = 0; i < len; i++) {
    const d = (pa[i] ?? 0) - (pb[i] ?? 0);
    if (d !== 0) return d > 0 ? 1 : -1;
  }
  return 0;
}

/**
 * Interroge la version publiée.
 * Retourne { disponible, distante } — `disponible` n'est vrai que si la version
 * en ligne est STRICTEMENT supérieure : un retour arrière de déploiement ne doit
 * pas déclencher une bannière de « mise à jour » qui ramènerait en arrière.
 */
export async function verifierVersion(fetchImpl = fetch) {
  try {
    const res = await fetchImpl(`/version.json?t=${Date.now()}`, { cache: "no-store" });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json();
    const distante = String(data.version ?? "");
    return {
      disponible: distante !== "" && compareVersions(distante, APP_VERSION) > 0,
      distante,
      erreur: false,
    };
  } catch {
    return { disponible: false, distante: null, erreur: true };
  }
}

/**
 * Force la mise à jour, de façon increvable.
 *
 * `location.reload()` seul ne suffit pas : sur une PWA iOS ajoutée à l'écran
 * d'accueil, il peut resservir la coquille en cache — c'est exactement le cas
 * où l'utilisateur croit devoir désinstaller puis réinstaller l'app. On purge
 * donc les service workers et les caches, puis on navigue avec un paramètre
 * neuf pour casser toute mise en cache d'URL.
 */
export async function forcerMiseAJour() {
  try {
    if ("serviceWorker" in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister()));
    }
  } catch {
    /* pas de service worker : rien à faire */
  }
  try {
    if ("caches" in window) {
      const cles = await caches.keys();
      await Promise.all(cles.map((k) => caches.delete(k)));
    }
  } catch {
    /* Cache API indisponible */
  }
  try {
    const url = new URL(window.location.href);
    url.searchParams.set("_u", String(Date.now()));
    window.location.replace(url.toString());
  } catch {
    window.location.reload();
  }
}
